import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { SOURCES, STATUSES, labelFor } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  AreaChart, Area, Cell,
} from "recharts";
import { DownloadSimple, ChartBar, Trophy, Buildings, Funnel as FunnelIcon, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRESETS = [
  { id: "7", label: "Last 7 days" },
  { id: "30", label: "Last 30 days" },
  { id: "90", label: "Last 90 days" },
  { id: "365", label: "Last 12 months" },
  { id: "all", label: "All time" },
];

const applyPreset = (id) => {
  if (id === "all") return { date_from: "", date_to: "" };
  const days = parseInt(id, 10);
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - days + 1);
  return {
    date_from: from.toISOString().slice(0, 10),
    date_to: to.toISOString().slice(0, 10),
  };
};

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [analytics, setAnalytics] = useState(null);
  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("30");
  const [filter, setFilter] = useState({ branch_id: "", agent_id: "", source: "", campaign_id: "", ...applyPreset("30") });

  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b.name])), [branches]);
  const agentMap = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a.name])), [agents]);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
      const [a, b, ag, cp] = await Promise.all([
        api.get("/analytics/overview", { params }),
        branches.length ? Promise.resolve({ data: branches }) : api.get("/branches"),
        agents.length ? Promise.resolve({ data: agents }) : api.get("/agents"),
        campaigns.length ? Promise.resolve({ data: campaigns }) : api.get("/campaigns"),
      ]);
      // Backend doesn't filter by `source`; do it client-side for now
      let data = a.data;
      if (filter.source) {
        // Re-fetch with full data and slice the source row out
        const onlyThisSource = filter.source;
        const subset = {
          ...data,
          total: data.by_source?.[onlyThisSource] || 0,
          by_source: { [onlyThisSource]: data.by_source?.[onlyThisSource] || 0 },
        };
        // re-derive converted/lost/open from source_status if available
        const row = data.source_status?.[onlyThisSource] || {};
        const conv = (row.converted || 0) + (row.booked || 0) + (row.existing_client || 0);
        const lost = row.lost || 0;
        subset.converted = conv;
        subset.lost = lost;
        subset.open = subset.total - conv - lost;
        subset.conversion_rate = subset.total ? +((conv / subset.total) * 100).toFixed(1) : 0;
        data = subset;
      }
      setAnalytics(data);
      setBranches(b.data);
      setAgents(ag.data);
      setCampaigns(cp.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter]);// eslint-disable-line react-hooks/exhaustive-deps

  const onPresetChange = (id) => {
    setPreset(id);
    setFilter((f) => ({ ...f, ...applyPreset(id) }));
  };

  const exportCsv = async (includeFilters = true) => {
    const params = new URLSearchParams();
    if (includeFilters) {
      Object.entries(filter).forEach(([k, v]) => { if (v) params.append(k, v); });
    }
    try {
      const res = await api.get(
        `/leads/export${params.toString() ? "?" + params.toString() : ""}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const tag = includeFilters && params.toString() ? "filtered" : "all";
      a.download = `leadflow_report_${tag}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(includeFilters && params.toString() ? "Filtered CSV downloaded" : "Full CSV downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const sourceRows = useMemo(() => {
    if (!analytics) return [];
    return SOURCES.map((s) => {
      const total = analytics.by_source?.[s.id] || 0;
      const row = analytics.source_status?.[s.id] || {};
      const converted = (row.converted || 0) + (row.booked || 0) + (row.existing_client || 0);
      const lost = row.lost || 0;
      const open = total - converted - lost;
      return {
        id: s.id, label: s.label, total, converted, lost, open,
        rate: total ? +((converted / total) * 100).toFixed(1) : 0,
      };
    }).sort((a, b) => b.total - a.total);
  }, [analytics]);

  const statusRows = useMemo(() => {
    if (!analytics) return [];
    const total = analytics.total || 0;
    return STATUSES.map((s) => {
      const count = analytics.by_status?.[s.id] || 0;
      return {
        id: s.id, label: s.label, color: s.color, count,
        share: total ? +((count / total) * 100).toFixed(1) : 0,
      };
    }).filter((r) => r.count > 0).sort((a, b) => b.count - a.count);
  }, [analytics]);

  const branchRows = useMemo(() => {
    if (!analytics) return [];
    const map = analytics.by_branch || {};
    return Object.entries(map)
      .filter(([id]) => id)
      .map(([id, count]) => ({ id, name: branchMap[id] || "—", count }))
      .sort((a, b) => b.count - a.count);
  }, [analytics, branchMap]);

  const agentRows = useMemo(() => {
    if (!analytics) return [];
    const map = analytics.by_agent || {};
    // We have totals per agent; conversion needs separate data (not in this endpoint)
    return Object.entries(map)
      .filter(([id]) => id)
      .map(([id, count]) => ({ id, name: agentMap[id] || "—", count }))
      .sort((a, b) => b.count - a.count);
  }, [analytics, agentMap]);

  const timeSeries = analytics?.time_series || [];

  if (loading && !analytics) {
    return (
      <div className="min-h-screen flex items-center justify-center text-xs uppercase tracking-[0.2em] text-muted-foreground">
        Loading reports…
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Insights"
        title="Reports"
        description="Aggregated performance across every source, status, branch and agent — sliced by your selected period."
        actions={
          <>
            <button
              onClick={load}
              data-testid="reports-refresh"
              className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors"
            >
              <ArrowsClockwise size={16} /> Refresh
            </button>
            <button
              onClick={() => exportCsv(true)}
              data-testid="reports-export-filtered"
              className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors"
            >
              <DownloadSimple size={16} /> Export filtered
            </button>
            <button
              onClick={() => exportCsv(false)}
              data-testid="reports-export-all"
              className="px-4 py-2.5 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm flex items-center gap-2 transition-colors"
            >
              <DownloadSimple size={16} /> Export all
            </button>
          </>
        }
      />

      {/* Filters */}
      <div className="px-8 py-4 border-b border-border bg-white flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-1" data-testid="reports-presets">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => onPresetChange(p.id)}
              data-testid={`preset-${p.id}`}
              className={`px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border ${
                preset === p.id
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-white hover:bg-secondary"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <input
          type="date" value={filter.date_from}
          onChange={(e) => { setPreset("custom"); setFilter({ ...filter, date_from: e.target.value }); }}
          data-testid="reports-date-from"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        />
        <span className="text-muted-foreground text-xs">→</span>
        <input
          type="date" value={filter.date_to}
          onChange={(e) => { setPreset("custom"); setFilter({ ...filter, date_to: e.target.value }); }}
          data-testid="reports-date-to"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        />
        <select
          value={filter.branch_id}
          onChange={(e) => setFilter({ ...filter, branch_id: e.target.value })}
          data-testid="reports-filter-branch"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        >
          <option value="">All branches</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        {isAdmin && (
          <select
            value={filter.agent_id}
            onChange={(e) => setFilter({ ...filter, agent_id: e.target.value })}
            data-testid="reports-filter-agent"
            className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
          >
            <option value="">All agents</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        <select
          value={filter.source}
          onChange={(e) => setFilter({ ...filter, source: e.target.value })}
          data-testid="reports-filter-source"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        >
          <option value="">All sources</option>
          {SOURCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select
          value={filter.campaign_id}
          onChange={(e) => setFilter({ ...filter, campaign_id: e.target.value })}
          data-testid="reports-filter-campaign"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none"
        >
          <option value="">All campaigns</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}{c.active === false ? " · paused" : ""}</option>)}
        </select>
      </div>

      {/* KPI strip */}
      <div className="px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="reports-kpis">
        <Kpi label="Total leads" value={analytics?.total ?? 0} color="#002FA7" icon={FunnelIcon} testid="kpi-total" />
        <Kpi label="Converted" value={analytics?.converted ?? 0} color="#008A00" icon={Trophy} testid="kpi-converted" />
        <Kpi label="Open" value={analytics?.open ?? 0} color="#52525B" testid="kpi-open" />
        <Kpi label="Conversion rate" value={`${analytics?.conversion_rate ?? 0}%`} color="#002FA7" testid="kpi-rate" />
      </div>

      {/* Time series */}
      <Section title="Lead volume" subtitle="Daily leads created (last 14 days)" testid="section-timeseries">
        <div className="bg-white border border-border p-4 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={timeSeries}>
              <defs>
                <linearGradient id="gTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#002FA7" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#002FA7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#e5e7eb" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} fontSize={11} />
              <YAxis fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="total" stroke="#002FA7" strokeWidth={2} fill="url(#gTotal)" name="Total" />
              <Area type="monotone" dataKey="converted" stroke="#008A00" strokeWidth={2} fillOpacity={0} name="Converted" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* Source performance */}
      <Section title="Source performance" subtitle="Conversion by source channel" testid="section-source">
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="bg-white border border-border p-4 h-72 lg:col-span-2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sourceRows.filter((r) => r.total > 0)}>
                <CartesianGrid stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="label" fontSize={10} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis fontSize={11} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="total" name="Total" fill="#002FA7" />
                <Bar dataKey="converted" name="Converted" fill="#008A00" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                  <th className="px-3 py-2 font-bold">Source</th>
                  <th className="px-3 py-2 font-bold text-right">Total</th>
                  <th className="px-3 py-2 font-bold text-right">Conv %</th>
                </tr>
              </thead>
              <tbody data-testid="source-table">
                {sourceRows.length === 0 ? (
                  <tr><td colSpan={3} className="px-3 py-6 text-center text-xs text-muted-foreground">No data</td></tr>
                ) : sourceRows.map((r) => (
                  <tr key={r.id} className="border-b border-border last:border-0" data-testid={`source-row-${r.id}`}>
                    <td className="px-3 py-2 text-xs">{r.label}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums font-semibold">{r.total}</td>
                    <td className="px-3 py-2 text-xs text-right tabular-nums text-[#008A00] font-semibold">{r.rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* Status breakdown */}
      <Section title="Status distribution" subtitle="Current lead pipeline" testid="section-status">
        <div className="bg-white border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3 font-bold text-right">Count</th>
                <th className="px-4 py-3 font-bold text-right">% of total</th>
                <th className="px-4 py-3 font-bold">Distribution</th>
              </tr>
            </thead>
            <tbody data-testid="status-table">
              {statusRows.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">No leads in this period</td></tr>
              ) : statusRows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0" data-testid={`status-row-${r.id}`}>
                  <td className="px-4 py-2.5">
                    <span className="text-xs uppercase tracking-[0.1em] font-bold" style={{ color: r.color }}>{r.label}</span>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-right tabular-nums font-semibold">{r.count}</td>
                  <td className="px-4 py-2.5 text-sm text-right tabular-nums text-muted-foreground">{r.share}%</td>
                  <td className="px-4 py-2.5">
                    <div className="h-2 bg-secondary w-full max-w-[240px]">
                      <div className="h-full" style={{ width: `${r.share}%`, backgroundColor: r.color }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Branch + Agent grids */}
      <div className="px-8 pb-8 grid lg:grid-cols-2 gap-6">
        <SectionInline title="Branch performance" icon={Buildings} testid="section-branch">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-4 py-2 font-bold">Branch</th>
                <th className="px-4 py-2 font-bold text-right">Leads</th>
              </tr>
            </thead>
            <tbody data-testid="branch-table">
              {branchRows.length === 0 ? (
                <tr><td colSpan={2} className="px-4 py-6 text-center text-xs text-muted-foreground">No data</td></tr>
              ) : branchRows.map((r, i) => (
                <tr key={r.id} className="border-b border-border last:border-0" data-testid={`branch-row-${r.id}`}>
                  <td className="px-4 py-2">
                    <span className="text-xs text-muted-foreground mr-2 tabular-nums">#{i + 1}</span>
                    <span className="text-sm">{r.name}</span>
                  </td>
                  <td className="px-4 py-2 text-sm text-right tabular-nums font-semibold">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionInline>

        <SectionInline title="Agent leaderboard" icon={Trophy} testid="section-agent">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-4 py-2 font-bold">Agent</th>
                <th className="px-4 py-2 font-bold text-right">Leads</th>
              </tr>
            </thead>
            <tbody data-testid="agent-table">
              {agentRows.length === 0 ? (
                <tr><td colSpan={2} className="px-4 py-6 text-center text-xs text-muted-foreground">No data</td></tr>
              ) : agentRows.map((r, i) => (
                <tr key={r.id} className="border-b border-border last:border-0" data-testid={`agent-row-${r.id}`}>
                  <td className="px-4 py-2">
                    <span className="text-xs text-muted-foreground mr-2 tabular-nums">#{i + 1}</span>
                    <span className="text-sm">{r.name}</span>
                  </td>
                  <td className="px-4 py-2 text-sm text-right tabular-nums font-semibold">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionInline>
      </div>

      {/* Source × Status matrix */}
      <Section title="Source × Status matrix" subtitle="Where each source channel ends up" testid="section-matrix">
        <div className="bg-white border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-3 py-3 font-bold sticky left-0 bg-white">Source ↓ / Status →</th>
                {STATUSES.map((s) => (
                  <th key={s.id} className="px-2 py-3 font-bold text-right whitespace-nowrap" title={s.label}>
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="matrix-table">
              {SOURCES.map((src) => {
                const row = analytics?.source_status?.[src.id] || {};
                const total = analytics?.by_source?.[src.id] || 0;
                if (total === 0) return null;
                return (
                  <tr key={src.id} className="border-b border-border last:border-0" data-testid={`matrix-row-${src.id}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white text-xs font-semibold">{src.label}</td>
                    {STATUSES.map((s) => {
                      const v = row[s.id] || 0;
                      return (
                        <td key={s.id} className="px-2 py-2 text-xs text-right tabular-nums"
                          style={{ color: v > 0 ? s.color : "#c4c4cc" }}>
                          {v > 0 ? v : "·"}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}

function Kpi({ label, value, color, icon: Icon, testid }) {
  return (
    <div className="border border-border bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</div>
        {Icon && <Icon size={16} weight="bold" style={{ color }} />}
      </div>
      <div className="font-display text-3xl font-bold tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function Section({ title, subtitle, children, testid }) {
  return (
    <div className="px-8 pb-6" data-testid={testid}>
      <div className="mb-3">
        <h2 className="font-display text-xl font-bold tracking-tight leading-none">{title}</h2>
        {subtitle && <div className="text-xs text-muted-foreground mt-1">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function SectionInline({ title, icon: Icon, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon && <Icon size={16} weight="bold" />}
        <h2 className="font-display text-base font-bold tracking-tight leading-none">{title}</h2>
      </div>
      <div className="bg-white border border-border overflow-hidden">{children}</div>
    </div>
  );
}
